import React, { useEffect, useMemo, useState } from "react";
import styles from "../../settings/components/Settings.module.css";
import { Option } from "../types";
import i18n from "i18next";

const EMPTY_SELECTED_VALUES: string[] = [];

const MOOD_SORT_ORDER = ["Anxiety", "Flow", "Confident", "Apathy"] as const;

export type MultiSelectProps = {
  options?: Option[] | undefined;
  handleClick?: (option: Option) => void;
  onSelectionChange?: (selectedValues: string[]) => void;
  initialSelectedValues?: string[];
  moodsTranslator?: any;
  causeTypes?: any;
  organizationId?: any;
  t?: any;
  type?: any;
  setActiveCauseTypes?: any;
  setActiveTeamCauseTypes?: any;
};

export const MultiSelect = ({
  options,
  handleClick,
  onSelectionChange,
  initialSelectedValues,
  causeTypes,
}: MultiSelectProps) => {
  const [checkedOptions, setCheckedOptions] = useState<Record<number, boolean>>(
    {}
  );
  const selectedValues = initialSelectedValues ?? EMPTY_SELECTED_VALUES;

  const visibleCauses = useMemo(() => {
    if (!causeTypes?.length) return [];

    const ordering: Record<string, number> = {};
    for (let i = 0; i < MOOD_SORT_ORDER.length; i++) {
      ordering[MOOD_SORT_ORDER[i]] = i;
    }
    const tail = MOOD_SORT_ORDER.length;

    return [...causeTypes]
      .filter((c) => c.archived !== true)
      .sort((a, b) => (ordering[a.mood] ?? tail) - (ordering[b.mood] ?? tail));
  }, [causeTypes]);

  const updateSelection = (next: Record<number, boolean>) => {
    if (options) {
      const nextSelected = options
        .filter((_, optionIndex) => Boolean(next[optionIndex]))
        .map((selectedOption) => selectedOption.title)
        .filter(
          (title): title is string =>
            typeof title === "string" && title.length > 0
        );
      onSelectionChange?.(nextSelected);
      return;
    }

    const nextSelectedCauses = visibleCauses
      .filter((_, optionIndex) => Boolean(next[optionIndex]))
      .map((cause) => String(cause.id ?? ""))
      .filter((causeId) => causeId.length > 0);
    onSelectionChange?.(nextSelectedCauses);
  };

  const setCheckedAtIndex = (idx: number, checked: boolean) => {
    setCheckedOptions((prev) => {
      const next = {
        ...prev,
        [idx]: checked,
      };
      updateSelection(next);
      return next;
    });
  };

  let optionTitlesKey;
  if (!options) {
    optionTitlesKey = null;
  } else {
    optionTitlesKey = options.map((o) => String(o?.title ?? "")).join("\u0001");
  }
  const selectedKey = [...selectedValues].sort().join("\u0001");

  useEffect(() => {
    const selectedSet = new Set(
      selectedValues.filter(
        (value): value is string =>
          typeof value === "string" && value.length > 0
      )
    );
    const nextCheckedOptions: Record<number, boolean> = {};

    if (options) {
      options.forEach((option, idx) => {
        nextCheckedOptions[idx] = Boolean(
          option.title && selectedSet.has(option.title)
        );
      });
      setCheckedOptions(nextCheckedOptions);
      return;
    }

    if (visibleCauses.length > 0) {
      visibleCauses.forEach((cause, idx) => {
        nextCheckedOptions[idx] = Boolean(
          cause.id && selectedSet.has(String(cause.id))
        );
      });
    }
    setCheckedOptions(nextCheckedOptions);
  }, [selectedKey, optionTitlesKey, visibleCauses]);

  return (
    <div className="select-component__duration-section">
      {visibleCauses?.length > 0 &&
        visibleCauses.map((cause, idx) => {
          return (
            <div key={cause.id}>
              <label
                className={`strength-button select-component__duration-button two-part-button ${styles.optionButton} ${styles["option-row"]}`}
                style={{
                  display: "flex",
                  justifyContent: "left",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => handleClick?.(cause)}
              >
                <input
                  type="checkbox"
                  checked={Boolean(checkedOptions[idx])}
                  onChange={(e) => setCheckedAtIndex(idx, e.target.checked)}
                  className={styles.settingsIconSpacing}
                  style={{
                    width: "16px",
                    height: "16px",
                    accentColor: "#ef4444",
                    cursor: "pointer",
                  }}
                />
                <span>
                  {cause.causeTypeTranslation &&
                  cause.causeTypeTranslation[i18n.language]
                    ? cause.causeTypeTranslation[i18n.language]
                    : cause.translation || "No translation available"}
                  <> | {cause.mood || cause.cause || "Unknown"}</>
                </span>
              </label>
            </div>
          );
        })}
      {options &&
        options.map((option, idx) => {
          return (
            <div key={idx} className={styles.optionRow}>
              <label
                className={`strength-button select-component__duration-button two-part-button ${styles.optionButton} ${styles["option-row"]}`}
                style={{
                  display: "flex",
                  justifyContent: "left",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => handleClick?.(option)}
              >
                <input
                  type="checkbox"
                  checked={Boolean(checkedOptions[idx])}
                  onChange={(e) => setCheckedAtIndex(idx, e.target.checked)}
                  className={styles.settingsIconSpacing}
                  style={{
                    width: "16px",
                    height: "16px",
                    accentColor: "#ef4444",
                    cursor: "pointer",
                  }}
                  aria-label={`Toggle ${option.title ?? "option"}`}
                />
                <span>{option.title}</span>
              </label>
            </div>
          );
        })}
    </div>
  );
};
